import React, { useState } from 'react';
import './BodyMap.css';
import { ZoomIn, RotateCcw } from 'lucide-react';

const BODY_PARTS = {
    Head: {
        label: 'Head & Neck',
        symptoms: ['Sever Headcahe', 'Dizziness', 'Vision Issues', 'Sore Throat', 'Neck Stain']
    },
    Chest: {
        label: 'Chest & Heart',
        symptoms: ['Chest Pain', 'Shortness of Breath', 'Palpitations', 'Cough', 'Heartburn']
    },
    Abdomen: {
        label: 'Stomach & Gut',
        symptoms: ['Stomach Pain', 'Nausea', 'Vomiting', 'Diarrhea', 'Constipation', 'Abdominal Bloating']
    },
    Arms: {
        label: 'Arms & Shoulders',
        symptoms: ['Shoulder Pain', 'Numbness in Fingers', 'Elbow Pain', 'Weakness', 'Rash on Arms']
    },
    Legs: {
        label: 'Legs & Knees',
        symptoms: ['Knee Pain', 'Swollen Ankles', 'Sciatica', 'Varicose Veins', 'Foot Pain']
    }
};

const BodyMap = ({ onSymptomSelect = (s) => console.log("Symptom selected:", s) }) => {
    // Debug log to confirm prop reception
    if (!onSymptomSelect) console.error("BodyMap: onSymptomSelect prop is missing!");
    const [hoveredPart, setHoveredPart] = useState(null);
    const [selectedPart, setSelectedPart] = useState(null);

    const handlePartClick = (partKey) => {
        setSelectedPart(partKey === selectedPart ? null : partKey);
    };

    const handleSymptomClick = (symptom) => {
        if (onSymptomSelect) {
            onSymptomSelect(symptom);
        }
    };

    const resetSelection = () => setSelectedPart(null);

    return (
        <div className="body-map-wrapper">
            <div className={`body-map-container ${selectedPart ? 'zoomed' : ''}`}>
                <div className="map-controls">
                    {selectedPart && (
                        <button className="reset-view-btn" onClick={resetSelection}>
                            <RotateCcw size={16} /> Back
                        </button>
                    )}
                </div>

                <svg viewBox="0 0 400 600" className={`human-body-svg ${selectedPart ? 'part-focused' : ''}`}>
                    <defs>
                        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* HEAD */}
                    <g
                        className={`body-part head ${selectedPart === 'Head' ? 'active' : ''} ${selectedPart && selectedPart !== 'Head' ? 'dimmed' : ''}`}
                        onClick={() => handlePartClick('Head')}
                        onMouseEnter={() => setHoveredPart('Head')}
                        onMouseLeave={() => setHoveredPart(null)}
                    >
                        <circle cx="200" cy="80" r="50" className="part-shape" />
                        <text x="200" y="80" className="part-label">HEAD</text>
                    </g>

                    {/* CHEST */}
                    <g
                        className={`body-part chest ${selectedPart === 'Chest' ? 'active' : ''} ${selectedPart && selectedPart !== 'Chest' ? 'dimmed' : ''}`}
                        onClick={() => handlePartClick('Chest')}
                        onMouseEnter={() => setHoveredPart('Chest')}
                        onMouseLeave={() => setHoveredPart(null)}
                    >
                        <path d="M 140 140 Q 200 140 260 140 L 250 240 Q 200 250 150 240 Z" className="part-shape" />
                        <text x="200" y="200" className="part-label">CHEST</text>
                    </g>

                    {/* ABDOMEN */}
                    <g
                        className={`body-part abdomen ${selectedPart === 'Abdomen' ? 'active' : ''} ${selectedPart && selectedPart !== 'Abdomen' ? 'dimmed' : ''}`}
                        onClick={() => handlePartClick('Abdomen')}
                        onMouseEnter={() => setHoveredPart('Abdomen')}
                        onMouseLeave={() => setHoveredPart(null)}
                    >
                        <path d="M 150 240 Q 200 250 250 240 L 240 340 Q 200 360 160 340 Z" className="part-shape" />
                        <text x="200" y="300" className="part-label">STOMACH</text>
                    </g>

                    {/* ARMS */}
                    <g
                        className={`body-part arms ${selectedPart === 'Arms' ? 'active' : ''} ${selectedPart && selectedPart !== 'Arms' ? 'dimmed' : ''}`}
                        onClick={() => handlePartClick('Arms')}
                        onMouseEnter={() => setHoveredPart('Arms')}
                        onMouseLeave={() => setHoveredPart(null)}
                    >
                        <rect x="90" y="140" width="40" height="180" rx="15" className="part-shape left-arm" />
                        <rect x="270" y="140" width="40" height="180" rx="15" className="part-shape right-arm" />
                        <text x="70" y="220" className="part-label side-label">L.ARM</text>
                        <text x="330" y="220" className="part-label side-label">R.ARM</text>
                    </g>

                    {/* LEGS */}
                    <g
                        className={`body-part legs ${selectedPart === 'Legs' ? 'active' : ''} ${selectedPart && selectedPart !== 'Legs' ? 'dimmed' : ''}`}
                        onClick={() => handlePartClick('Legs')}
                        onMouseEnter={() => setHoveredPart('Legs')}
                        onMouseLeave={() => setHoveredPart(null)}
                    >
                        <rect x="150" y="350" width="45" height="220" rx="15" className="part-shape" />
                        <rect x="205" y="350" width="45" height="220" rx="15" className="part-shape" />
                        <text x="200" y="450" className="part-label">LEGS</text>
                    </g>
                </svg>
            </div>

            {/* SYMPTOM SELECTOR CARD */}
            <div className={`symptom-panel ${selectedPart ? 'visible' : ''}`}>
                {selectedPart && (
                    <>
                        <h4>{BODY_PARTS[selectedPart].label}</h4>
                        <div className="symptom-grid">
                            {BODY_PARTS[selectedPart].symptoms.map((sym, idx) => (
                                <button key={idx} className="symptom-btn" onClick={() => handleSymptomClick(sym)}>
                                    {sym}
                                </button>
                            ))}
                        </div>
                    </>
                )}
                {!selectedPart && <p className="helper-text">Tap a body part to see symptoms</p>}
            </div>
        </div>
    );
};

export default BodyMap;
